import React, { useState, useEffect, useRef } from "react";
import {
  IoChatbubblesSharp,
  IoClose,
  IoExpand,
  IoRemove,
  IoContract,
} from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import send from "../assets/animated-send.gif";
import loader from "../assets/animated-loader.gif";
import chatBackground from "../assets/chatBackground.png";
import okidioki from "../assets/okidoki-o-grey.png";
import bot from "../assets/bot.gif";
import person from "../assets/person.gif";
import { aiInstance } from "../axiosInstance";
import sidebarData from "./sidebar/sidebarData";
import * as XLSX from "xlsx";

function ChatBubble({ isOpen, setIsOpen, isMinimized, setIsMinimized, chatPos }) {
  const navigate                        = useNavigate();
  const [messages, setMessages]         = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isBotTyping, setIsBotTyping]   = useState(false);
  const [firstTime, setFirstTime]       = useState(false);
  const messagesEndRef                  = useRef(null);
  const inputRef                        = useRef();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isBotTyping]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !firstTime) {
      setFirstTime(true);
      sendToAPI("hi", true);
    }
  }, [isOpen]);

  // ── Download Excel ──────────────────────────────────────────────────────────
  const downloadExcel = (data, toolName) => {
    if (!data || !data.length) return;
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Data");
    const fileName = `${(toolName || "data").replace(/_/g, "-")}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // ── Send message ────────────────────────────────────────────────────────────
  const sendToAPI = async (text, silent = false) => {
    if (!silent) {
      setMessages(prev => [...prev, { sender: "user", text }]);
    }
    setIsBotTyping(true);
    try {
      const res            = await aiInstance.post("/chat", { message: text });
      const reply          = res.data.reply          || "Sorry, I could not understand that.";
      const tool           = res.data.tool           || null;
      const data           = res.data.data           || null;
      const isList         = res.data.is_list        || false;
      const statusColor    = res.data.status_color   || null;
      const statusName     = res.data.status_name    || null;
      const navigationUrl  = res.data.navigation_url || null;

      setMessages(prev => [...prev, {
        sender:        "bot",
        text:          reply,
        tool:          tool,
        data:          data,
        isList:        isList,
        statusColor:   statusColor,
        statusName:    statusName,
        navigationUrl: navigationUrl,
      }]);
    } catch (err) {
      console.error("ChatBubble API error:", err);
      setMessages(prev => [...prev, {
        sender:  "bot",
        text:    err.response?.status === 400
          ? "⚠️ Your message is too long. Please keep it under 1000 characters."
          : "⚠️ Could not connect to the assistant. Please try again.",
        isError: true,
      }]);
    } finally {
      setIsBotTyping(false);
    }
  };
const handleSend = () => {
    if (isBotTyping) return;
    const text = inputMessage.trim();
    if (!text) return;
    setInputMessage("");
    sendToAPI(text);
  };
// ── Find navigation route from bot reply using sidebarData ────────────
const findRouteFromReply = (text) => {
    if (!text) return null;

    const firstLine = text.split("\n")[0].trim();

    if (!firstLine.toLowerCase().startsWith("go to") && !firstLine.includes(">")) return null;

    // Flatten all sidebar items into a single array
    const allItems = [];
    Object.values(sidebarData).forEach(category => {
      category.forEach(item => {
        if (item.linkName) allItems.push({ label: item.label, linkName: item.linkName });
        if (item.items) {
          item.items.forEach(sub => {
            if (sub.linkName) allItems.push({ label: sub.label, linkName: sub.linkName });
          });
        }
      });
    });

    // Normalize text: remove extra spaces, handle "blacklist" vs "black list" etc.
    const normalize = (str) => str.toLowerCase().replace(/\s+/g, "");
    const firstLineNorm = normalize(firstLine);

    // Find the longest matching label in the first line
    let bestMatch = null;
    let bestLen = 0;

    allItems.forEach(item => {
      const labelLower = item.label.toLowerCase();
      const labelNorm = normalize(item.label);

      // Try exact match first
      if (firstLine.toLowerCase().includes(labelLower) && labelLower.length > bestLen) {
        bestMatch = item;
        bestLen = labelLower.length;
      }
      // Try normalized match (handles "blacklist" vs "black list")
      else if (firstLineNorm.includes(labelNorm) && labelNorm.length > bestLen) {
        bestMatch = item;
        bestLen = labelNorm.length;
      }
    });

    return bestMatch;
  };
  const handleClose = () => {
    setIsOpen(false);
    setMessages([]);
    setFirstTime(false);
    setIsFullScreen(false);
    setInputMessage("");
  };

  // ── Render text with optional status badge ──────────────────────────────────
  const renderText = (text, staColor, staName) => {
    if (!text) return null;
    return (
      <>
        {text.split("\n").map((line, i) => (
          <React.Fragment key={i}>
            {line}
            {i < text.split("\n").length - 1 && <br />}
          </React.Fragment>
        ))}

        {/* Status Color Badge */}
        {staColor && staName && (
          <div style={{
            display:      "inline-flex",
            alignItems:   "center",
            gap:          "6px",
            marginTop:    "8px",
            padding:      "4px 10px",
            borderRadius: "20px",
            background:   staColor + "22",
            border:       `1px solid ${staColor}`,
            fontSize:     "11px",
            fontWeight:   600,
            color:        staColor,
          }}>
            <span style={{
              width:        "8px",
              height:       "8px",
              borderRadius: "50%",
              background:   staColor,
              display:      "inline-block",
              flexShrink:   0,
            }} />
            {staName}
          </div>
        )}
      </>
    );
  };

  // Calculate safe chat box position
  const windowWidth = typeof window !== "undefined" ? window.innerWidth : 1200;
  const windowHeight = typeof window !== "undefined" ? window.innerHeight : 800;

  // Center of the dragged button
  const buttonCx = windowWidth - 20 - 25 + (chatPos?.x || 0);
  const buttonCy = windowHeight - 70 - 25 + (chatPos?.y || 0);

  // Clamp the chat box position to viewport
  const chatWidth = 380;
  const chatHeight = 520;
  const chatLeft = Math.max(10, Math.min(buttonCx - chatWidth / 2, windowWidth - chatWidth - 10));
  const chatTop = Math.max(10, Math.min(buttonCy - chatHeight / 2, windowHeight - chatHeight - 10));

  return (
    <div className="chat-bubble-container">
      {isOpen && !isMinimized && (
        <div className={`chat-box ${isFullScreen ? "full-screen" : ""}`}
          style={{
            position:      "fixed",
            top:           isFullScreen ? "0"     : `${chatTop}px`,
            left:          isFullScreen ? "0"     : `${chatLeft}px`,
            bottom:        "auto",
            right:         "auto",
            transform:     "none",
            width:         isFullScreen ? "100vw" : `${chatWidth}px`,
            height:        isFullScreen ? "100vh" : `${chatHeight}px`,
            display:       "flex",
            flexDirection: "column",
            borderRadius:  isFullScreen ? "0" : "12px",
            boxShadow:     "0 8px 32px rgba(0,0,0,0.18)",
            zIndex:        1200,
            overflow:      "hidden",
            background:    "white",
          }}
        >
          {/* ── Header ── */}
          <div className="chat-header" style={{
            display:    "flex",
            alignItems: "center",
            padding:    "10px 14px",
            background: "#8d1212",
            color:      "white",
            fontWeight: 600,
            fontSize:   "14px",
            gap:        "8px",
            flexShrink: 0,
          }}>
            <img src={okidioki} style={{ width: "20px", height: "20px" }} alt="CargoTool" />
            <span style={{ flex: 1 }}>OKIDOKI Assistant</span>
            {isFullScreen
              ? <IoContract onClick={() => setIsFullScreen(false)} style={{ cursor: "pointer", fontSize: "18px" }} />
              : <IoExpand   onClick={() => setIsFullScreen(true)}  style={{ cursor: "pointer", fontSize: "18px" }} />
            }
            <IoRemove onClick={() => setIsMinimized(true)} style={{ cursor: "pointer", fontSize: "18px" }} />
            <IoClose  onClick={handleClose}                style={{ cursor: "pointer", fontSize: "18px" }} />
          </div>

          {/* ── Messages ── */}
          <div style={{
            flex:               1,
            overflowY:          "auto",
            padding:            "12px",
            backgroundImage:    `url('${chatBackground}')`,
            backgroundSize:     "cover",
            backgroundPosition: "center",
            display:            "flex",
            flexDirection:      "column",
            gap:                "10px",
          }}>
            {messages.map((msg, i) => (
              <div key={i} style={{
                display:       "flex",
                flexDirection: msg.sender === "user" ? "row-reverse" : "row",
                alignItems:    "flex-end",
                gap:           "6px",
              }}>
                <img
                  src={msg.sender === "bot" ? bot : person}
                  style={{ width: "24px", height: "24px", borderRadius: "50%", flexShrink: 0 }}
                  alt={msg.sender}
                />
                <div style={{
                  display:       "flex",
                  flexDirection: "column",
                  gap:           "4px",
                  alignItems:    msg.sender === "user" ? "flex-end" : "flex-start",
                }}>
                  {/* Message bubble */}
                  <div style={{
                    maxWidth:     "85%",
                    padding:      "9px 12px",
                    borderRadius: msg.sender === "user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                    background:   msg.sender === "user"
                      ? "#8d1212"
                      : msg.isError ? "#fff3f3" : "white",
                    color:      msg.sender === "user" ? "white" : "#222",
                    fontSize:   "13px",
                    lineHeight: "1.55",
                    boxShadow:  "0 1px 4px rgba(0,0,0,0.10)",
                    border:     msg.isError ? "1px solid #ffcccc" : "none",
                  }}>
                    {renderText(msg.text, msg.statusColor, msg.statusName)}
                  </div>

                  {/* ── Download Excel button ── */}
                  {msg.isList && msg.data && msg.data.length > 0 && (
                    <button
                      onClick={() => downloadExcel(msg.data, msg.tool)}
                      style={{
                        display:      "flex",
                        alignItems:   "center",
                        gap:          "5px",
                        background:   "#1a7a4a",
                        color:        "white",
                        border:       "none",
                        borderRadius: "8px",
                        padding:      "5px 10px",
                        fontSize:     "11px",
                        fontWeight:   600,
                        cursor:       "pointer",
                        boxShadow:    "0 1px 4px rgba(0,0,0,0.15)",
                      }}
                    >
                      📥 Download Excel ({msg.data.length} records)
                    </button>
                  )}

                 {/* ── Navigation link ── */}
                  {(() => {
                    const backendUrl = msg.navigationUrl;
                    const autoNav = !backendUrl && msg.tool === "search_user_manual"
                      ? findRouteFromReply(msg.text)
                      : null;

                    const navUrl   = backendUrl || (autoNav ? autoNav.linkName : null);
                    const navLabel = autoNav
                      ? autoNav.label
                      : navUrl && navUrl.includes("/gps")
                        ? "GPS Location"
                        : navUrl
                          ? navUrl.split("?")[0].split("/").pop().replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase())
                          : "";
                    if (!navUrl) return null;

                    return (
                      <button
                        onClick={() => {
                          navigate(navUrl);
                          setIsMinimized(true);
                        }}
                        style={{
                          display:        "inline-flex",
                          alignItems:     "center",
                          gap:            "6px",
                          marginTop:      "4px",
                          padding:        "6px 12px",
                          borderRadius:   "8px",
                          background:     "#EFF6FF",
                          border:         "1px solid #BFDBFE",
                          color:          "#2563EB",
                          fontSize:       "11px",
                          fontWeight:     600,
                          cursor:         "pointer",
                          transition:     "background 0.2s",
                        }}
                        onMouseEnter={e => e.target.style.background = "#DBEAFE"}
                        onMouseLeave={e => e.target.style.background = "#EFF6FF"}
                      >
                        {navUrl && navUrl.includes("/gps") ? "📍" : "📋"} Go to {navLabel} →
                      </button>
                    );
                  })()}
                </div>
              </div>
            ))}

            {isBotTyping && (
              <div style={{ display: "flex", alignItems: "flex-end", gap: "6px" }}>
                <img src={bot} style={{ width: "24px", height: "24px", borderRadius: "50%" }} alt="bot" />
                <img src={loader} style={{ width: "44px", height: "26px" }} alt="Typing..." />
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* ── Input ── */}
          <div style={{
            display:    "flex",
            alignItems: "center",
            padding:    "8px 10px",
            borderTop:  "1px solid #eee",
            background: "white",
            gap:        "8px",
            flexShrink: 0,
          }}>
          <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={e => setInputMessage(e.target.value)}
              onKeyPress={e => e.key === "Enter" && !isBotTyping && handleSend()}
              placeholder="Type here..."
              style={{
                flex:         1,
                border:       "1px solid #ddd",
                borderRadius: "20px",
                padding:      "8px 14px",
                fontSize:     "13px",
                outline:      "none",
              }}
            />
            <button
              onClick={handleSend}
              disabled={isBotTyping}
              style={{
                background: "none",
                border:     "none",
                cursor:     isBotTyping ? "not-allowed" : "pointer",
                padding:    0,
                display:    "flex",
                alignItems: "center",
                opacity:    isBotTyping ? 0.4 : 1,
              }}
            >
              <img src={send} style={{ width: "26px", height: "26px" }} alt="Send" />
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

export default ChatBubble;